import axios from "../../axios";
import * as actionTypes from "./actionTypes";
import { UPDATE_SITE_CONFIGURATION_FAIL } from "./actionTypes";
import { showToast } from "./message";
import {
  handleError,
  reportError,
  VALIDATION_FAILURE,
} from "../../utils/errorHandling";
import { stringifyMinimal } from "../../utils/browser";
import { AppDispatch } from "../../store";
import { PaginatedResponseEnvelope } from "../../types/types";
import { DataSourceResponse } from "../../types/dataSources";
import { SiteConfigurationState } from "../reducers/siteConfiguration";
import siteConfigurationFormat from "../../utils/formatConverters/siteConfigurationFormat";

export const getSiteConfiguration = (tenantId: string) => async (dispatch) => {
  try {
    dispatch(getDataSources());
    dispatch({ type: actionTypes.GET_SITE_CONFIGURATION_START });
    const res = await axios.get<
      PaginatedResponseEnvelope<DataSourceResponse[]>
    >("/api/v1/tenant_config/" + tenantId);

    onReceiveTenantConfig(dispatch, res.data.data);
  } catch (error: any) {
    let handleErrors;
    if (!error.response) {
      reportError(error);
      handleErrors = ["An error has occurred."];
    } else {
      handleErrors = error.response.data.errors
        ? Object.values(error.response.data.errors).map(
            (error: any) => error[0]
          )
        : [error.response.data.message];
    }
    showToast(actionTypes.GET_SITE_CONFIGURATION_FAIL, handleErrors, "danger");
  }
};

export function onReceiveTenantConfig(dispatch, tenantConfig) {
  dispatch({
    type: actionTypes.GET_SITE_CONFIGURATION_SUCCESS,
    results: tenantConfig,
  });
}

export const getDataSources = () => async (dispatch: AppDispatch) => {
  try {
    const query = { perPage: 1000, includeFields: 1 };
    const res = await axios.get(
      "/api/v1/data_sources" + stringifyMinimal(query, { addQueryPrefix: true })
    );

    dispatch({
      type: actionTypes.GET_DATA_SOURCES_SUCCESS,
      results: { sources: res?.data?.data },
    });
  } catch (error: any) {
    const handleErrors = error.response?.data?.errors
      ? Object.values(error.response.data.errors).map((error: any) => error[0])
      : [error.response?.data?.message];
    showToast(actionTypes.GET_DATA_SOURCES_FAIL, handleErrors, "danger");
  }
};

export function updateSiteConfiguration(config: SiteConfigurationState) {
  return async (dispatch: AppDispatch) => {
    try {
      await doUpdateSiteConfiguration(dispatch, config);
    } catch (error) {
      const normalizedError = handleError(error);

      dispatch({
        type: UPDATE_SITE_CONFIGURATION_FAIL,
        errors:
          normalizedError.type === VALIDATION_FAILURE
            ? normalizedError.data
            : [normalizedError.message],
      });
    }
  };
}

async function doUpdateSiteConfiguration(
  dispatch: AppDispatch,
  config: SiteConfigurationState
) {
  dispatch({
    type: actionTypes.UPDATE_SITE_CONFIGURATION_START,
  });

  const { tenant } = config;

  const nonFileRequestData = siteConfigurationFormat.local.toRequest(config);
  const formData = new FormData();

  let anyFilesToUpload = false;
  for (const fileKey of ["logo", "background", "favicon"] as const) {
    const fileObject = config[fileKey];
    if (fileObject.file) {
      formData.append(fileKey, fileObject.file);
      anyFilesToUpload = true;
    } else if (!fileObject.imageName) {
      nonFileRequestData[fileKey] = null;
    }
  }

  const url = `/api/v1/tenant_config/${tenant.uuid}`;
  if (anyFilesToUpload) {
    await axios.post(url, formData);
  }
  const res = await axios.post(url, nonFileRequestData);

  dispatch({
    type: actionTypes.UPDATE_TENANT_CONFIG,
    results: { tenantConfig: res.data.data },
  });

  onReceiveTenantConfig(dispatch, res.data.data);

  showToast(
    actionTypes.UPDATE_SITE_CONFIGURATION_SUCCESS,
    ["Site Configuration Updated"],
    "success"
  );
}

export function recacheMenuFilters(tenant: { uuid: string }) {
  return async (dispatch: AppDispatch) => {
    dispatch({ type: actionTypes.RECACHE_MENU_FILTERS_START });

    try {
      await axios.post("/api/v1/tenant_config/" + tenant.uuid, {
        recacheMenuFiltersNow: 1,
      });

      dispatch({ type: actionTypes.RECACHE_MENU_FILTERS_SUCCESS });
      showToast(
        actionTypes.RECACHE_MENU_FILTERS_FAIL,
        ["Menu Filters Refreshed."],
        "success"
      );
    } catch (error) {
      dispatch({ type: actionTypes.RECACHE_MENU_FILTERS_FAIL });
      handleError(error);
    }
  };
}

export const recacheMenuFilter = (uuid: string) => (dispatch: AppDispatch) => {
  dispatch({
    type: actionTypes.RECACHE_MENU_FILTER_START,
    meta: {
      api: {
        method: "POST",
        endpoint: `/api/v1/menu_filters/${uuid}/recache`,
        toastOnFailure: true,
      },
      toasts: [
        {
          type: "success",
          title: "Menu Filter Refreshed.",
          condition: actionTypes.RECACHE_MENU_FILTER_SUCCESS,
        },
      ],
    },
    uuid,
  });
};
